import type { SingleLegacyPlan } from 'types/legacyDashboard';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import { CheckboxField } from '@hologram-dimension/checkbox-field';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useRefinementList } from 'react-instantsearch';
import styles from './ProfileFilter.module.css';
import useCoverageProfiles from '../useCoverageProfiles';

interface ProfileFilterFormProps {
  profile: string;
}

interface PlanProfileFilterProps {
  planProfiles?: SingleLegacyPlan['profiles'];
}

const PlanProfileFilter = (props: PlanProfileFilterProps) => {
  const { planProfiles } = props;
  const { refine, items } = useRefinementList({
    attribute: 'offer_id',
    limit: 100,
  });
  const { sendAnalyticsEvent } = useAnalytics();
  const { register } = useForm<ProfileFilterFormProps>();
  const profiles = useCoverageProfiles({ planProfiles });
  const options = useMemo(() => {
    const result: Array<{
      label: string;
      value: string;
      labelNode: React.ReactNode;
      defaultChecked: boolean;
    }> = [];
    profiles?.map((profile) => {
      const matchingItem = items.find((item) => item.value === profile.offerId!.toString());
      return result.push({
        label: profile.profileName ?? 'Unknown',
        value: profile.offerId!.toString(),
        labelNode: (
          <span>
            {profile.profileName ?? <span className={styles.diminishedText}>Unknown</span>}
            &nbsp;&nbsp;
            <span className={styles.diminishedText}>
              ({matchingItem?.count ?? 0}
              {profile.installationState === 'ENABLED' && ', active'})
            </span>
          </span>
        ),
        defaultChecked: matchingItem?.isRefined ?? false,
      });
    });
    return result;
  }, [profiles, items]);

  // We don't need to show this filter if there's only one profile
  if (!profiles || profiles.length <= 1) return null;

  return (
    <CheckboxField
      label="Coverage by profile"
      options={options}
      {...register('profile')}
      onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
        refine(target.value);
        if (sendAnalyticsEvent && target.checked) {
          sendAnalyticsEvent('Network Coverage - Filter - Single Profile', {
            profile: (target as HTMLInputElement).value,
          });
        }
      }}
    />
  );
};

export default PlanProfileFilter;
